import { DollarSign, Users, Percent, Activity, ArrowUpRight, ArrowDownRight, TrendingUp, CheckCircle2, XCircle, Clock, Zap, UserPlus, BellRing, FileCog, Timer, AlertTriangle, RefreshCw } from 'lucide-react'
import { kpiStats, recentTransactions, activityFeed, topPlans } from '../../../Models/dashboardData'
import { useDashboard } from '../../../Controllers/useDashboard'
import { useSession } from '../../../Controllers/useSession'

const ICONS = { revenue: <DollarSign size={18} />, users: <Users size={18} />, conversion: <Percent size={18} />, uptime: <Activity size={18} /> }
const TX = { success: { cls: 'badge-active', label: 'Paid' }, pending: { cls: 'badge-pending', label: 'Pending' }, failed: { cls: 'badge-danger', label: 'Failed' } }
const ACT_IC = { user: <Zap size={12} />, system: <CheckCircle2 size={12} />, warning: <Clock size={12} />, error: <XCircle size={12} /> }

export default function Dashboard() {
  const { revenueRef, userRef, loading } = useDashboard()
  const { timeStr, isWarn, pct, resetSession } = useSession()

  const getFormattedDate = () => {
    const now = new Date()
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    }).format(now)
  }

  return (
    <div className="dash-page page-in">

      {/* Header */}
      <div className="dash-hero">
        <div className="dash-hero-glow" />
        <div className="dash-ph">
          <div>
            <p className="dash-overline">Dashboard</p>
            <h1 className="page-title" style={{ marginBottom: 4 }}>Welcome back, Admin</h1>
            <p style={{ color: 'var(--text3)', fontSize: 'var(--fs-sm)' }}>
              {getFormattedDate()} · Here is the live performance snapshot.
            </p>
          </div>

          {/* Session Timer Block */}
          <div className={`session-timer-block${isWarn ? ' session-warn' : ''}`}>
            <div className="session-timer-top">
              <span className="session-icon">
                {isWarn ? <AlertTriangle size={14} /> : <Timer size={14} />}
              </span>
              <span className="session-label">
                {isWarn ? 'Expires soon' : 'Session active'}
              </span>
              <div className="session-time"> :- {timeStr}</div>
              {/* <button className="session-refresh" onClick={resetSession} title="Extend session">
                <RefreshCw size={12} />
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Expires soon banner */}
      {isWarn && (
        <div className="warn-banner">
          <AlertTriangle size={15} />
          <span>Your session expires in <strong>{timeStr}</strong>. Click refresh on the timer to extend.</span>
        </div>
      )}

      <div className="dash-ph">
        <div>
          <h3 style={{ color: 'var(--text1)', fontSize: 'var(--fs-lg)', fontWeight: 600 }}>Key Performance Metrics</h3>
          <p style={{ color: 'var(--text3)', fontSize: 'var(--fs-sm)' }}>Real-time business health and growth indicators</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <span className="badge badge-active">Live Data</span>
          <span className="badge badge-info">Auto refresh: 60s</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        {kpiStats.map(({ id, label, value, change, up, color, colorGlow ,variant}) => (
          <div key={id} className={`stat-card stat-card--${variant}`}>
            <span style={{ position: 'absolute', top: 0, right: 0, width: 110, height: 110, borderRadius: '50%', background: colorGlow, filter: 'blur(30px)', transform: 'translate(30%,-30%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)', position: 'relative' }}>
              <div className="stat-card-icon" style={{ background: colorGlow, color }}>{ICONS[id]}</div>
              <span className={`stat-card-trend ${up ? 'up' : 'down'}`} style={{ color: color, background: `${color}15`, border: `1px solid ${colorGlow}` }}>
                {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{change}
              </span>
            </div>
            <div className="stat-card-value" style={{ position: 'relative' }}>{value}</div>
            <div className="stat-card-label">{label}</div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', background: `linear-gradient(90deg,${color}88,transparent)`, borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }} />
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div className="quick-actions stat-grid col-3">
        <button className="card q-action">
          <span className="q-icon"><UserPlus size={15} /></span>
          <div><strong>Add User</strong><p>Create account and assign plan</p></div>
        </button>
        <button className="card q-action">
          <span className="q-icon"><BellRing size={15} /></span>
          <div><strong>Send Notification</strong><p>Publish system update instantly</p></div>
        </button>
        <button className="card q-action">
          <span className="q-icon"><FileCog size={15} /></span>
          <div><strong>Generate Report</strong><p>Create monthly executive summary</p></div>
        </button>
      </div>

      {/* Charts */}
      <div className="charts-row">
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">Revenue Overview</div><div className="card-subtitle">Monthly vs target</div></div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span className="leg"><span style={{ background: 'var(--accent)' }} />Revenue</span>
              <span className="leg"><span style={{ background: 'rgba(99,102,241,.35)' }} />Target</span>
              <span className="badge badge-gold">2025</span>
            </div>
          </div>
          <div style={{ position: 'relative', height: 210 }}>
            {loading ? <div className="skeleton" style={{ height: '100%', borderRadius: 'var(--radius-md)' }} /> : <canvas ref={revenueRef} />}
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">User Acquisition</div><div className="card-subtitle">New vs active this week</div></div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span className="leg"><span style={{ background: 'rgba(79,70,229,.8)' }} />New</span>
              <span className="leg"><span style={{ background: 'rgba(6,182,212,.5)' }} />Active</span>
              <span className="badge badge-cyan">7 Days</span>
            </div>
          </div>
          <div style={{ position: 'relative', height: 210 }}>
            {loading ? <div className="skeleton" style={{ height: '100%', borderRadius: 'var(--radius-md)' }} /> : <canvas ref={userRef} />}
          </div>
        </div>
      </div>

      {/* Widgets */}
      <div className="widgets-row">
        <div className="card" style={{ padding: 0 }}>
          <div className="wdg-hdr">
            <span className="wdg-title">Recent Transactions</span>
            <button className="btn btn-ghost btn-sm">View all</button>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr><th>ID</th><th>User</th><th>Plan</th><th>Amount</th><th>Status</th><th>Time</th></tr></thead>
              <tbody>
                {recentTransactions.map(tx => (
                  <tr key={tx.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{tx.id}</td>
                    <td style={{ fontWeight: 500, color: 'var(--text1)' }}>{tx.user}</td>
                    <td>{tx.plan}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{tx.amount}</td>
                    <td><span className={`badge ${TX[tx.status].cls}`}>{TX[tx.status].label}</span></td>
                    <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>{tx.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <div className="card" style={{ flex: 1 }}>
            <div className="card-header">
              <div className="card-title">Activity Feed</div>
              <span className="badge badge-live">Live</span>
            </div>
            {activityFeed.map((a, i) => (
              <div key={i} className="act-row" style={{ borderBottom: i < activityFeed.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                <span className="act-ic" style={{ background: `${a.color}18`, color: a.color }}>{ACT_IC[a.type]}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text2)', lineHeight: 1.4 }}>{a.msg}</p>
                  <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text4)', marginTop: 2 }}>{a.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }
        .dash-hero {
          position: relative;
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-active);
          background: linear-gradient(135deg, rgba(79, 70, 229, .2), rgba(6, 182, 212, .12));
          overflow: hidden;
          padding: clamp(1rem, 2vw, 1.5rem);
        }
        .dash-overline { font-size:var(--fs-xs); text-transform:uppercase; letter-spacing:.08em; color:var(--accent-hover); font-weight:700; margin-bottom:4px; }
        .dash-hero-glow { position:absolute; width:280px; height:280px; right:-70px; top:-90px; border-radius:50%; background:rgba(79,70,229,.22); filter:blur(45px); pointer-events:none; }
        .dash-ph { display:flex; justify-content:space-between; align-items:flex-end; flex-wrap:wrap; gap:var(--space-md); }
        .kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:var(--space-md); }
        .widgets-row { display:grid; grid-template-columns:1.5fr 1fr; gap:var(--space-md); align-items:start; }
        .quick-actions { display:grid; grid-template-columns:repeat(3,1fr); gap:var(--space-md); }
        .q-action { text-align:left; padding:var(--space-md); display:flex; align-items:flex-start; gap:var(--space-sm); cursor:pointer; }
        .q-action strong { color:var(--text1); font-size:var(--fs-sm); display:block; margin-bottom:2px; }
        .q-action p { color:var(--text3); font-size:var(--fs-xs); }
        .q-icon { width:30px; height:30px; border-radius:8px; display:grid; place-items:center; color:var(--accent-hover); background:rgba(79,70,229,.12); border:1px solid var(--border); flex-shrink:0; }
        .wdg-hdr { display:flex; justify-content:space-between; align-items:center; padding:var(--space-md) var(--space-lg); border-bottom:1px solid var(--border); }
        .wdg-title { font-size:var(--fs-md); font-weight:600; color:var(--text1); }
        .leg { display:flex; align-items:center; gap:5px; font-size:var(--fs-xs); color:var(--text3); }
        .leg span { width:8px; height:8px; border-radius:2px; flex-shrink:0; display:inline-block; }
        .prog-track { height:4px; background:var(--bg-raised); border-radius:var(--radius-full); overflow:hidden; }
        .prog-fill { height:100%; border-radius:var(--radius-full); transition:width .8s cubic-bezier(.4,0,.2,1); }
        .act-row { display:flex; gap:var(--space-sm); padding:var(--space-sm) 0; align-items:flex-start; }
        .act-ic { width:26px; height:26px; border-radius:var(--radius-sm); display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:1px; }

        /* Session Timer */
        .session-timer-block {
          display: flex;
          justify-content: center;
          align-items: center;
          align-self: center; 
          gap: 5px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 10px 14px;
          min-width: 170px;
          transition: border-color .2s, background .2s;
          backdrop-filter: blur(10px);
        }
        .session-timer-block.session-warn {
          border-color: rgba(245,158,11,.45);
          background: rgba(245,158,11,.07);
        }
        .session-timer-top {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }
        .session-icon {
          display: flex;
          align-items: center;
          color: #6D7AA5;
          flex-shrink: 0;
        }
        .session-warn .session-icon { color: var(--color-amber); }
        .session-label {
          font-size: var(--fs-xs);
          font-weight: 600;
          color: #6D7AA5;
          text-transform: uppercase;
          letter-spacing: .07em;
          flex: 1;
        }
        .session-warn .session-label { color: var(--color-amber); }
        .session-time {
          font-family: var(--font-mono);
          font-size: var(--fs-xl);
          font-weight: 800;
          color: var(--text1);
          letter-spacing: -0.02em;
          line-height: 1;
          align-self: flex-start;
        }
        .session-warn .session-time { color: var(--color-amber); }
        .session-bar-track {
          width: 100%;
          height: 3px;
          background: var(--bg-raised);
          border-radius: var(--radius-full);
          overflow: hidden;
        }
        .session-bar-fill {
          height: 100%;
          border-radius: var(--radius-full);
          transition: width 1s linear, background .5s ease;
        }
        .session-hint {
          font-size: 10px;
          color: var(--text4);
          align-self: flex-start;
          margin-top: 1px;
        }
        .session-warn .session-hint { color: rgba(245,158,11,.6); }

        /* Warn Banner */
        .warn-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(245,158,11,.1);
          border: 1px solid rgba(245,158,11,.3);
          border-radius: var(--radius-md);
          padding: 10px 16px;
          font-size: var(--fs-sm);
          color: var(--color-amber);
          animation: soft-rise .3s ease;
        }
        .warn-banner strong { font-weight: 700; }

        @media(max-width:1100px){ .kpi-grid{grid-template-columns:repeat(2,1fr);} }
        @media(max-width:600px){ .session-timer-block{min-width:140px;} .session-time{font-size:var(--fs-xl);} }
      `}</style>
    </div>
  )
}